import { isRejectedWithValue, Middleware, MiddlewareAPI } from '@reduxjs/toolkit';

import { withSentry } from 'infrastructure/services/sentryLazy';
import { setErrorStatus, setShowError } from 'infrastructure/redux/slices/auth.slice';

// Circular-safe JSON.stringify. The previous implementation passed a replacer
// that itself called JSON.stringify on every object value, which infinite-
// recurses for any non-null object and overflows Hermes's native stack.
export const safeStringify = (value: unknown): string | undefined => {
  if (value === undefined) return undefined;
  const seen = new WeakSet<object>();
  return JSON.stringify(value, (_key, nested) => {
    if (typeof nested === 'object' && nested !== null) {
      if (seen.has(nested as object)) return '[Circular]';
      seen.add(nested as object);
    }
    return nested;
  });
};

export const rtkQueryErrorLogger: Middleware = (api: MiddlewareAPI) => (next) => (action) => {
  if (isRejectedWithValue(action)) {
    // Endpoints can opt out of the global error Snackbar / Sentry capture by
    // setting `skipGlobalErrorHandler: true` on their `transformErrorResponse`.
    if (action?.payload?.skipGlobalErrorHandler) {
      return next(action);
    }

    const errorStatusCode = action?.payload?.status;
    const isStatusCodeToLog = errorStatusCode > 399;

    if (isStatusCodeToLog) {
      const request = action?.meta?.baseQueryMeta?.request;
      const method = request?.method;
      const url = request?.url;
      const requestBody = safeStringify(action?.meta?.arg?.originalArgs?.body);
      const responseBody = action?.payload?.data;

      api.dispatch(setErrorStatus(errorStatusCode));
      api.dispatch(setShowError(true));

      withSentry((Sentry) => {
        Sentry.withScope((scope) => {
          scope.setExtra('Request method', method);
          scope.setExtra('Request URL', url);
          scope.setExtra('Request body', requestBody);
          scope.setExtra('Response code', errorStatusCode);
          scope.setExtra('Response body', responseBody);

          Sentry.captureException(new Error(JSON.stringify(action?.payload?.data?.message)));
        });
      });
    }
  }

  return next(action);
};
